import React, { useState, useEffect } from "react";
import axios from "axios";
import { Search, FileText } from "lucide-react";

function Circular() {
    const [search, setSearch] = useState("");
    const [circulars, setCirculars] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        axios.get( `${process.env.REACT_APP_MINF_BAC_BASE}/circulars`)
            .then(response => {
                setCirculars(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching circulars:", error);
                setLoading(false);
            });
    }, []);

    const filteredCirculars = [...circulars]
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .filter((circular) =>
        circular.department.toLowerCase().includes(search.toLowerCase()) ||
        circular.title.toLowerCase().includes(search.toLowerCase())
    );

    const image = "url('https://minf-minf.s3.ap-south-1.amazonaws.com/814f97f5-951e-4178-aefb-69bea61ad93e.jpg')";

    return (
        <section className="bg-transparent mb-12 relative">
            <div
                className="mx-5 mb-12 rounded-lg "
                style={{ backgroundImage: image, backgroundSize: "cover" }}
            >
                <div className="flex md:h-[40vh] h-[50vh] justify-center rounded-lg items-center bg-[rgba(0,0,0,0.7)]">
                    <p className="px-8 md:text-8xl text-5xl text-center text-white tracking-widest mt-[80px] interf pb-2">
                        CIRCULARS
                    </p>
                </div>
            </div>
            <div className="max-w-7xl mx-auto md:px-0 px-8 mb-8">
                <div className="relative flex">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by Title or Department"
                        className="w-full pl-12 pr-4 py-3 rounded-lg border-2 border-gray-700 bg-transparent focus:border-gray-700 focus:ring-1 focus:ring-gray-700 transition-all outline-none"
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>
            <div className="container mx-auto px-4 pb-12">
                <div className="rounded-3xl shadow-xl overflow-hidden border border-gray-200 bg-white">
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="min-w-full">
                            <thead>
                                <tr className="border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">S.No</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Department</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Attachment</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="text-center py-8 text-xl text-gray-400 font-semibold">Loading...</td>
                                    </tr>
                                ) : filteredCirculars.length === 0 ? (
                                    <tr>
                                        <td className="px-6 py-4 text-sm text-center text-gray-600" colSpan={5}>
                                            No Data Available
                                        </td>
                                    </tr>
                                ) : (
                                    filteredCirculars.map((circular, index) => (
                                        <tr key={circular.id} className="group hover:bg-gray-50 transition-all duration-300 ease-in-out cursor-pointer">
                                            <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">{index + 1}</td>
                                            <td className="px-6 py-3 text-sm font-medium whitespace-nowrap">{circular.department}</td>
                                            <td className="px-6 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">{circular.title}</td>
                                            <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">
                                                {new Date(circular.date).toLocaleDateString('en-US', {
                                                    year: 'numeric', month: 'short', day: 'numeric'
                                                })}
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap">
                                                <a href={circular.attachment} target="_blank" rel="noopener noreferrer">
                                                    <button className="inline-flex items-center gap-2 text-gray-600 hover:text-red-500 text-sm font-medium transition-colors duration-200">
                                                        <FileText className="h-4 w-4" />
                                                        View
                                                    </button>
                                                </a>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Mobile Card View */}
                    <div className="md:hidden">
                        {loading ? (
                            <div className="text-center py-8 text-xl text-gray-400 font-semibold">Loading...</div>
                        ) : filteredCirculars.length === 0 ? (
                            <div className="text-center py-8 text-xl text-gray-400 font-semibold">No Data Available</div>
                        ) : (
                            filteredCirculars.map((circular, index) => (
                                <div key={circular.id} className="mb-4 bg-white rounded-2xl shadow p-4 flex flex-col gap-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-xs font-semibold text-gray-400">#{index + 1}</span>
                                        <span className="flex items-center gap-1 text-xs text-gray-500">
                                            <FileText className="h-4 w-4 text-gray-400" />
                                            <span>{new Date(circular.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                                        </span>
                                    </div>
                                    <div className="mb-1">
                                        <span className="block text-base font-bold text-gray-800 leading-tight">{circular.title}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                                        <span className="inline-block bg-gray-100 px-2 py-1 rounded-full font-medium">
                                            <svg className="inline-block w-4 h-4 mr-1 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 01-8 0m8 0a4 4 0 00-8 0m8 0V5a4 4 0 00-8 0v2m8 0a4 4 0 01-8 0m8 0v2a4 4 0 01-8 0V7" /></svg>
                                            {circular.department}
                                        </span>
                                    </div>
                                    <div className="mt-2 flex">
                                        <a href={circular.attachment} target="_blank" rel="noopener noreferrer" className="w-full">
                                            <button className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white py-2 rounded-lg font-semibold text-sm hover:bg-gray-800 transition-colors duration-200">
                                                <FileText className="h-4 w-4" />
                                                View Attachment
                                            </button>
                                        </a>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
                <div className="text-xl flex justify-center mt-5 text-gray-500">
                    {filteredCirculars.length} {filteredCirculars.length === 1 ? 'Circular' : 'Circulars'}
                </div>
            </div>
        </section>
    );
}

export default Circular;
